import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Container, Stack, Button, Card, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { EditorState, convertFromHTML, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { stateToHTML } from 'draft-js-export-html';
import { toast } from 'react-toastify';
import {
  getContactPageContent,
  setContactPageContent,
  getContactData,
  setContactData,
  setContactPagePhoto,
  getContactPagePhoto
} from '../utils/requests/aboutRequest';
import UploadPreview from '../components/uploads/UploadPreview';

/**
 * @typedef ContactData
 * @property {number} id
 * @property {string} key
 * @property {string} value
 * @property {string} link
 */

/**
 * get header for card
 * @param {{children: any}} props
 * @return {JSX.Element}
 */
function HeaderText(props) {
  return (
    <Typography variant="caption" sx={{ ml: 2, mb: 1, mt: 1 }}>
      {props.children}
    </Typography>
  );
}

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 2;

const getListStyle = () => ({
  padding: grid,
  overflow: 'auto'
});

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  padding: grid * 2,
  margin: `0 0 ${grid}px 0`,
  ...draggableStyle
});

export default function ContactPage() {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isLoading, setIsLoading] = useState(false);
  /**
   * @type {ContactData[]}
   */
  const initItems = [];
  const [items, setItems] = useState(initItems);
  const [pen, setPen] = useState(null);
  const [photo, setPhoto] = useState();
  const [file, setFile] = useState();

  const navigate = useNavigate();

  useEffect(() => getData(), []);

  function onFileChange(e) {
    setFile(e.target.files[0]);
  }

  function getData() {
    getContactPageContent().then((content) => {
      const blocksFromHTML = convertFromHTML(content);
      const state = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      setEditorState(EditorState.createWithContent(state));
      setIsLoading(false);
    });

    getContactData().then((dataList) => {
      setItems(dataList);
    });

    getContactPagePhoto().then((photo) => {
      setPhoto(photo);
    });
  }

  function save() {
    const html = stateToHTML(editorState.getCurrentContent());
    setContactPageContent(html).then((response) => {
      toast.success(response.data.message);
    });

    setContactData(items).then((response) => {
      toast.success(response.data.message);
    });

    if (!file) return;
    setContactPagePhoto(file).then((response) => {
      toast.success(response.data.message);
    });
  }

  const addContact = () => {
    /**
     * @type {ContactData[]}
     */
    const newItems = [...items];
    newItems.push({ key: '', value: '', link: '' });
    setItems(newItems);
  };

  const updateItemData = (key, value, index) => {
    const newItems = [...items];
    newItems[index][key] = value;
    setItems(newItems);
  };

  function onDragEnd(result) {
    if (!result.destination) {
      return;
    }
    const newItems = reorder(items, result.source.index, result.destination.index);
    setItems(newItems);
  }

  function deleteItem(index) {
    const newItems = [...items];
    delete newItems[index];
    setItems(newItems);
  }

  return (
    <Container>
      <Typography variant="h4" sx={{ mb: 5 }}>
        Customize Contact page
      </Typography>
      <Stack direction="column" sx={{ mb: 2 }} spacing={2}>
        <Stack direction="row" spacing={2}>
          <UploadPreview currentPhoto={photo} onChange={(e) => onFileChange(e)} />
          <Card className="editor" style={{ minHeight: 250 }} sx={{ p: 1 }}>
            <HeaderText>Contact text:</HeaderText>
            <Editor
              editorState={editorState}
              onEditorStateChange={(e) => setEditorState(e)}
              toolbar={{
                inline: { inDropdown: true },
                list: { inDropdown: true },
                textAlign: { inDropdown: true },
                link: { inDropdown: true },
                history: { inDropdown: true },
                image: { inDropdown: false }
              }}
            />
          </Card>
        </Stack>
        <Card sx={{ height: 530, width: '100%' }}>
          <HeaderText>Contact data:</HeaderText>
          <Stack sx={{ height: '500px' }} direction="column">
            <DragDropContext onDragEnd={(e) => onDragEnd(e)} style={{ height: '100%' }}>
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={getListStyle(snapshot.isDraggingOver)}
                  >
                    {items.map((item, index) => (
                      <Draggable key={index} draggableId={`item${index}`} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                          >
                            <Stack
                              spacing={2}
                              sx={{
                                background: '#9a9a9a08',
                                padding: '5px',
                                borderRadius: '1em'
                              }}
                            >
                              <Typography sx={{ textAlign: 'center' }}>...</Typography>
                              <Stack spacing={1} direction="row">
                                <TextField
                                  sx={{ width: '100%' }}
                                  label="Key"
                                  value={item.key}
                                  onChange={(e) =>
                                    updateItemData('key', e.currentTarget.value, index)
                                  }
                                />
                                <TextField
                                  multiline
                                  sx={{ width: '100%' }}
                                  label="Value"
                                  value={item.value}
                                  onChange={(e) =>
                                    updateItemData('value', e.currentTarget.value, index)
                                  }
                                />
                              </Stack>
                              <TextField
                                label="Link (Optional)"
                                value={item.link}
                                onChange={(e) =>
                                  updateItemData('link', e.currentTarget.value, index)
                                }
                              />
                              <Button color="error" onClick={() => deleteItem(index)}>
                                Delete
                              </Button>
                            </Stack>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <Button onClick={() => addContact()}>Add new contact</Button>
          </Stack>
        </Card>
        <Stack direction="row">
          <LoadingButton loading={isLoading} variant="contained" onClick={() => save()}>
            Save
          </LoadingButton>
          <Button onClick={() => navigate('/')}>Cancel</Button>
        </Stack>
      </Stack>
    </Container>
  );
}
